import { createNativeStackNavigator } from "@react-navigation/native-stack";
import { createStackNavigator } from "@react-navigation/stack";
import { ProjectManagementStackParamList } from "../types";
import React, { useContext, useEffect, useState } from 'react';
import { Platform, Pressable, ScrollView, TouchableOpacity, Animated, LayoutAnimation } from "react-native";
import IndividualProjectPage from "../screens/MidLevel/ProjectManagementScreen/IndividualProjectPage";
import CompletedProjects from "../screens/MidLevel/ProjectManagementScreen/CompletedProjects";
import Projects from "../screens/MidLevel/ProjectManagementScreen/ProjectManagementScreen";
import ProjectTemplate from "../screens/MidLevel/ProjectManagementScreen/ProjectTemplate";
import ProjectFavourites from "../screens/MidLevel/ProjectManagementScreen/ProjectFavourites";
import ProjectMenu from "../screens/MidLevel/ProjectManagementScreen/ProjectMenu";
import { Title, View, Text } from "@constants/Themed";
import { themeSelector } from "@reduxLocal/selectors/themeSelector";
import CreatorDisplay from "@components/Global/UserDisplay/CreatorDisplay";
import { ToolbarContext } from "@hooks/toolbarContext";
import moment from "moment";
import { H2, H5 } from "tamagui";
import { AntDesign, MaterialIcons } from "@expo/vector-icons";
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import IndividualProjectPageModern from "../screens/MidLevel/ProjectManagementScreen/IndividualProjectPageModern";
import ModernProjectHeader from "../screens/MidLevel/ProjectManagementScreen/Components/Modern/ModernProjectHeader";

const ProjectManagementStack = createStackNavigator<ProjectManagementStackParamList>();

// Expandable Header Component
const ExpandableProjectHeader = ({ route, navigation }) => {
  const theme = themeSelector();
  const globalDate = useSelector((state: DefaultRootState) => state.UI.dateFormat);
  const {
    project,
    projectData, 
    headerProps,
    activeTab: routeActiveTab
  } = route.params || {};

  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState<'work_packages' | 'kanban' | 'gantt' | 'team' | 'analytics' | 'files' | 'settings'>(
    routeActiveTab || 'work_packages'
  );

  useEffect(() => {
    if (routeActiveTab && routeActiveTab !== activeTab) {
      setActiveTab(routeActiveTab);
    }
  }, [routeActiveTab]);

  const tabs = [
    { key: 'work_packages' as const, title: 'Work Packages', icon: 'layers' },
    { key: 'kanban' as const, title: 'Kanban', icon: 'view-column' },
    { key: 'gantt' as const, title: 'Gantt', icon: 'timeline' },
    { key: 'team' as const, title: 'Team', icon: 'group' },
    { key: 'analytics' as const, title: 'Analytics', icon: 'analytics' },
    { key: 'files' as const, title: 'Files', icon: 'folder' },
    { key: 'settings' as const, title: 'Settings', icon: 'settings' },
  ];

  const handleToggleExpand = () => {
    LayoutAnimation.configureNext(LayoutAnimation.Presets.easeInEaseOut);
    setIsExpanded(!isExpanded);
  };

  const handleTabSelect = (tabKey: typeof tabs[number]['key']) => {
    setActiveTab(tabKey);
    navigation.setParams({
      ...route.params,
      activeTab: tabKey
    });
  };

  if (headerProps) {
    return <ModernProjectHeader navigation={navigation} />;
  }

  return (
    <View style={{ 
      backgroundColor: theme ? '#FFFFFF' : '#1F2937',
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.15,
      shadowRadius: 12,
      elevation: 8,
      borderBottomLeftRadius: 24,
      borderBottomRightRadius: 24,
      borderBottomWidth: 1,
      borderBottomColor: theme ? '#E5E7EB' : '#374151',
      overflow: 'hidden'
    }}>
      {/* Collapsed View - Title + Arrow */}
      <View style={{
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 24,
        paddingVertical: 12,
        minHeight: 60
      }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
          <Pressable
            onPress={() => navigation.navigate('ProjectsList')}
            style={({ pressed }) => ({
              padding: 12,
              borderRadius: 12,
              backgroundColor: pressed
                ? (theme ? '#F3F4F6' : '#374151')
                : (theme ? '#F9FAFB' : '#374151'),
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: 16
            })}
          >
            <AntDesign
              name="arrow-left"
              size={20}
              color={theme ? '#1F2937' : '#F9FAFB'}
            />
          </Pressable>

          <View style={{ flex: 1 }}>
            <H5
              numberOfLines={1}
              allowFontScaling={false}
              adjustsFontSizeToFit
              style={{
                color: theme ? '#1F2937' : '#F9FAFB', 
                fontSize: 16,
                fontWeight: '700',
                textAlign: 'left'
              }}
            >
              {project?.title || projectData?.title || "Project"}
            </H5>
          </View>
        </View>

        <TouchableOpacity
          onPress={handleToggleExpand}
          style={{
            padding: 8,
            top: 5,
            borderRadius: 8,
            backgroundColor: theme ? '#F3F4F6' : '#374151',
            marginLeft: 12
          }}
        >
          <AntDesign
            name={isExpanded ? "down" : "up"}
            size={20}
            color={theme ? '#1F2937' : '#F9FAFB'}
          />
        </TouchableOpacity>
      </View>

      {/* Expanded View */}
      {isExpanded && (
        <View>
          {(project?.end_date || projectData?.end_date) && (
            <View style={{ 
              paddingHorizontal: 24,
              paddingBottom: 12
            }}>
              <View style={{ 
                flexDirection: 'row',
                alignItems: 'center',
                backgroundColor: theme ? '#FEF3C7' : '#365314',
                paddingHorizontal: 12,
                paddingVertical: 4,
                borderRadius: 12,
                alignSelf: 'flex-start'
              }}>
                <AntDesign
                  name="calendar"
                  size={12}
                  color={theme ? '#D97706' : '#84CC16'}
                  style={{ marginRight: 6 }}
                />
                <Text style={{
                  color: theme ? '#D97706' : '#84CC16',
                  fontSize: 11,
                  fontWeight: '600'
                }}>
                  Due: {moment(project?.end_date || projectData?.end_date).format(globalDate)}
                </Text>
              </View>
            </View>
          )}

          <View style={{
            borderTopWidth: 1,
            borderTopColor: theme ? '#E5E7EB' : '#374151',
            paddingVertical: 8
          }}>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={{
                paddingHorizontal: 24,
                gap: 8
              }}
            >
              {tabs.map((tab) => {
                const isActive = activeTab === tab.key;
                return (
                  <TouchableOpacity
                    key={tab.key}
                    onPress={() => handleTabSelect(tab.key)}
                    style={{
                      paddingHorizontal: 16,
                      paddingVertical: 8,
                      borderRadius: 20,
                      backgroundColor: isActive
                        ? (theme ? '#3B82F6' : '#2563EB')
                        : (theme ? '#F3F4F6' : '#374151'),
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: 6,
                      marginRight: 4
                    }}
                  >
                    <MaterialIcons
                      name={tab.icon as any}
                      size={18}
                      color={isActive
                        ? '#FFFFFF'
                        : (theme ? '#6B7280' : '#9CA3AF')
                      }
                    />
                    <Text style={{
                      fontSize: 13,
                      fontWeight: isActive ? '600' : '500',
                      color: isActive
                        ? '#FFFFFF'
                        : (theme ? '#6B7280' : '#9CA3AF')
                    }}>
                      {tab.title}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
          </View>
        </View>
      )}
    </View>
  );
};

const ProjectManagementNavigator = () => {
  const theme = themeSelector();
  return (
    <ProjectManagementStack.Navigator
      screenOptions={{
        headerShown: false,
        cardStyle: { backgroundColor: theme ? Platform.select({ web: "white" }) : 'black' }
      }}
      initialRouteName="ProjectMenu"
    >
      <ProjectManagementStack.Screen options={{ title: "Project Menu" }} name="ProjectMenu" component={Platform.OS === 'web' ? Projects : ProjectMenu} />
      <ProjectManagementStack.Screen options={{ title: "Projects" }} name="ProjectsList" component={Projects} />
      <ProjectManagementStack.Screen name='IndividualProjectPage'
        options={({ route, navigation }) => ({
          headerShown: true,
          header: () => <ExpandableProjectHeader route={route} navigation={navigation} />
        })}

            component={Platform.OS === 'ios' ? IndividualProjectPage : IndividualProjectPageModern} />
      <ProjectManagementStack.Screen name="CompletedProjects" component={CompletedProjects} />
      <ProjectManagementStack.Screen name="Templates" options={{ title: "Templates" }} component={ProjectTemplate} />
      <ProjectManagementStack.Screen name="ProjectFavourites" options={{ title: "Project Favourites" }} component={ProjectFavourites} />
      <ProjectManagementStack.Screen name="ActiveProjects" component={Projects} />
      <ProjectManagementStack.Screen name="ProjectManagement" component={Projects} />
    </ProjectManagementStack.Navigator>
  )
}

export { ProjectManagementStack, ProjectManagementNavigator };